import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import API from '../../../services/API'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import MFALogin from '../../../helpers/auth/MFALogin'
import useIsMobile from '../../../helpers/useIsMobile'
import OutstandingJourneys from '../components/OutstandingJourneys'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import AddLocationIcon from '@mui/icons-material/AddLocation'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import SearchIcon from '@mui/icons-material/Search'

const WAITING_STATUSES = ['Planned', 'Assigned']
const ACTIVE_STATUSES = [
  'Crew Acknowledged',
  'Mobile',
  'On Scene',
  'Patient Onboard',
  'Transporting',
  'Arrived',
  'Handover Complete',
]
const EXCEPTION_STATUSES = ['Cancelled', 'No Show', 'Aborted']

const STATUS_CHIP_COLORS = {
  Planned: 'default',
  Assigned: 'info',
  'Crew Acknowledged': 'primary',
  Mobile: 'primary',
  'On Scene': 'primary',
  'Patient Onboard': 'primary',
  Transporting: 'primary',
  Arrived: 'primary',
  'Handover Complete': 'primary',
  Completed: 'success',
  Cancelled: 'error',
  'No Show': 'warning',
  Aborted: 'error',
}

const statusGroup = (status) => {
  if (WAITING_STATUSES.includes(status)) return 'Waiting'
  if (ACTIVE_STATUSES.includes(status)) return 'Active'
  if (status === 'Completed') return 'Completed'
  if (EXCEPTION_STATUSES.includes(status)) return 'Exceptions'
  return 'Waiting'
}

const DATE_PRESETS = [
  {
    label: 'Today',
    range: () => [moment().startOf('day'), moment().endOf('day')],
  },
  {
    label: 'Tomorrow',
    range: () => [
      moment().add(1, 'day').startOf('day'),
      moment().add(1, 'day').endOf('day'),
    ],
  },
  {
    label: 'Next 7 Days',
    range: () => [
      moment().startOf('day'),
      moment().add(6, 'days').endOf('day'),
    ],
  },
  {
    label: 'This Month',
    range: () => [moment().startOf('month'), moment().endOf('month')],
  },
  {
    label: 'Past 7 Days',
    range: () => [
      moment().subtract(7, 'days').startOf('day'),
      moment().endOf('day'),
    ],
  },
]

// On a phone the two date pickers share the row with the prev/next arrows, so
// they flex to fit and drop the input padding a touch; desktop is untouched.
const dateFieldSx = {
  flex: { xs: 1, md: '0 0 auto' },
  minWidth: 0,
  '& .MuiInputBase-input': {
    px: { xs: 1, md: 1.75 },
    fontSize: { xs: '0.875rem', md: '1rem' },
  },
}

const StatTile = ({ label, value, caption, color, active, onClick }) => (
  <Card
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 1.5,
      flex: 1,
      minWidth: 120,
      cursor: 'pointer',
      borderWidth: active ? 2 : 1,
      borderColor: active ? `${color}.main` : 'divider',
      backgroundColor: active ? 'action.hover' : 'background.paper',
      transition: 'border-color 120ms ease, background-color 120ms ease',
      '&:hover': { borderColor: `${color}.main` },
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: '1.6rem', fontWeight: 800, lineHeight: 1.2 }}
      color={`${color}.main`}
    >
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {caption}
    </Typography>
  </Card>
)

const PTSBookings = () => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const isPhone = useIsMobile('sm')
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [requiresMFA, setRequiresMFA] = useState(false)
  const [bookings, setBookings] = useState([])
  const [startDate, setStartDate] = useState(moment().startOf('day'))
  const [endDate, setEndDate] = useState(moment().add(6, 'days').endOf('day'))
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [groupFilter, setGroupFilter] = useState(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 400)
    return () => clearTimeout(timer)
  }, [query])

  const fetchBookings = useCallback(async () => {
    setLoading(true)
    setFetchError(false)
    try {
      const res = await API.post('/pts/search-bookings', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        query: debouncedQuery,
      })
      if (res.status === 200) {
        setBookings(res.data)
      } else if (res.data?.errorCode === 'notAuthorised') {
        setRequiresMFA(true)
      } else {
        setFetchError(true)
      }
    } catch (err) {
      console.error(err)
      setFetchError(true)
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, debouncedQuery])

  useEffect(() => {
    fetchBookings()
  }, [fetchBookings])

  const setRange = (start, end) => {
    setStartDate(start)
    setEndDate(end)
  }

  // Shift the window backwards/forwards by its own length, so "Next 7 Days"
  // pages a week at a time and "Today" pages a day at a time.
  const windowDays = Math.max(endDate.diff(startDate, 'days') + 1, 1)
  const shiftWindow = (direction) =>
    setRange(
      moment(startDate).add(direction * windowDays, 'days'),
      moment(endDate).add(direction * windowDays, 'days'),
    )

  const activePreset = DATE_PRESETS.find(({ range }) => {
    const [presetStart, presetEnd] = range()
    return (
      presetStart.isSame(startDate, 'day') && presetEnd.isSame(endDate, 'day')
    )
  })?.label

  const counts = useMemo(() => {
    const c = { Waiting: 0, Active: 0, Completed: 0, Exceptions: 0 }
    bookings.forEach((row) => {
      c[statusGroup(row.status)] += 1
    })
    return c
  }, [bookings])

  const visibleRows = useMemo(
    () =>
      groupFilter
        ? bookings.filter((row) => statusGroup(row.status) === groupFilter)
        : bookings,
    [bookings, groupFilter],
  )

  const toggleGroupFilter = (group) =>
    setGroupFilter((prev) => (prev === group ? null : group))

  const columns = useMemo(
    () => [
      {
        field: 'sortTime',
        headerName: 'Date',
        minWidth: isMobile ? 92 : 100,
        flex: 0.5,
        renderCell: ({ row }) =>
          row?.sortTime ? moment(row.sortTime).format('ddd DD MMM') : '',
      },
      {
        field: 'bookingRef',
        headerName: 'Reference',
        minWidth: isMobile ? 100 : 110,
        flex: 0.6,
        valueGetter: (value, row) =>
          row?.bookingDoc?.bookingRef ?? row?.journeyRef ?? '',
        renderCell: ({ row }) => (
          <Stack sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" noWrap>
              {row?.bookingDoc?.bookingRef ?? row?.journeyRef ?? ''}
            </Typography>
            {row?.bookingDoc?.source === 'Hospital Portal' && (
              <Tooltip
                title={
                  row?.bookingDoc?.status === 'Pending'
                    ? 'Requested via the hospital portal — awaiting review'
                    : 'Requested via the hospital portal'
                }
              >
                <Chip
                  size="small"
                  label={
                    row?.bookingDoc?.status === 'Pending'
                      ? 'Portal · review'
                      : 'Portal'
                  }
                  color={
                    row?.bookingDoc?.status === 'Pending'
                      ? 'warning'
                      : 'default'
                  }
                  variant={
                    row?.bookingDoc?.status === 'Pending'
                      ? 'filled'
                      : 'outlined'
                  }
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 800,
                    borderRadius: 1,
                    alignSelf: 'flex-start',
                  }}
                />
              </Tooltip>
            )}
            {row?.bookingDoc?.source === 'Crew' &&
              (() => {
                const needsReview =
                  row.bookingDoc.crewJob?.reviewRequired &&
                  !row.bookingDoc.crewJob?.reviewedAt
                return (
                  <Tooltip
                    title={
                      needsReview
                        ? 'Added by the crew on their duty — check it before it is invoiced'
                        : 'Added by the crew on their duty'
                    }
                  >
                    <Chip
                      size="small"
                      label={needsReview ? 'Crew · review' : 'Crew'}
                      color={needsReview ? 'warning' : 'default'}
                      variant={needsReview ? 'filled' : 'outlined'}
                      sx={{
                        height: 18,
                        fontSize: 10,
                        fontWeight: 800,
                        borderRadius: 1,
                        alignSelf: 'flex-start',
                      }}
                    />
                  </Tooltip>
                )
              })()}
            {(row?.journeyType === 'Risk Assessment' ||
              row?.bookingDoc?.journeyType === 'Risk Assessment') && (
              <Tooltip title="Home risk assessment visit - no patient transport">
                <Chip
                  size="small"
                  icon={<MonitorHeartOutlinedIcon sx={{ fontSize: 13 }} />}
                  label="Risk Assessment"
                  color="info"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 800,
                    borderRadius: 1,
                    alignSelf: 'flex-start',
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: 'patient',
        headerName: 'Patient',
        minWidth: isMobile ? 110 : 130,
        flex: 0.8,
        valueGetter: (value, row) =>
          `${row?.patientDoc?.demographics?.firstName ?? ''} ${
            row?.patientDoc?.demographics?.lastName ?? ''
          }`.trim(),
      },
      {
        field: 'pickup',
        headerName: 'Pickup',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" noWrap>
              {row?.pickup?.name || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row?.pickup?.when?.patientReadyFrom
                ? `Ready ${moment(row.pickup.when.patientReadyFrom).format('HH:mm')}`
                : 'Any time'}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'dropoff',
        headerName: 'Dropoff',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" noWrap>
              {row?.dropoff?.name || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row?.dropoff?.when?.targetArrivalTime
                ? `Arrive by ${moment(row.dropoff.when.targetArrivalTime).format('HH:mm')}`
                : 'Any time'}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: isMobile ? 110 : 130,
        flex: 0.6,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={row.status}
            color={STATUS_CHIP_COLORS[row.status] || 'default'}
            variant={row.status === 'Planned' ? 'outlined' : 'filled'}
            sx={{ fontWeight: 700, borderRadius: 1 }}
          />
        ),
      },
      {
        field: 'organisation',
        headerName: 'Organisation',
        flex: 0.7,
        minWidth: 120,
        valueGetter: (value, row) =>
          row?.bookingDoc?.patientIsCustomer
            ? 'Patient'
            : (row?.orgDoc?.orgName ?? ''),
      },
    ],
    [isMobile],
  )

  // On a phone only the identifying columns fit; pickup/dropoff/organisation
  // (and, below 600px, the reference) are all on the booking page a row tap
  // opens, and the reference is still searchable from the box above.
  const columnVisibilityModel = useMemo(
    () =>
      isMobile
        ? {
            pickup: false,
            dropoff: false,
            organisation: false,
            ...(isPhone ? { bookingRef: false } : {}),
          }
        : {},
    [isMobile, isPhone],
  )

  if (requiresMFA)
    return (
      <MFALogin
        onSuccess={() => {
          setRequiresMFA(false)
          fetchBookings()
        }}
      />
    )

  return (
    <Stack gap={2}>
      <Stack
        gap={1}
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ lg: 'center' }}
      >
        <Box>
          <Typography variant="h6">Patient Transport Bookings</Typography>
          <Typography variant="body2" color="text.secondary">
            {startDate.format('ddd DD MMM')} – {endDate.format('ddd DD MMM')}
            {debouncedQuery ? ` · matching “${debouncedQuery}”` : ''}
          </Typography>
        </Box>
        <Button
          startIcon={<AddLocationIcon />}
          onClick={() => navigate('/pts/bookings/new-booking')}
          variant="contained"
          color="anMainBlue"
        >
          New Booking
        </Button>
      </Stack>

      <OutstandingJourneys />

      {/* --- Controls --- */}
      <Card variant="outlined" sx={{ p: 1.5 }}>
        <Stack gap={1.5}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            gap={1.5}
            alignItems={{ md: 'center' }}
          >
            <TextField
              placeholder="Search reference, patient, organisation, postcode…"
              variant="outlined"
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack
              direction="row"
              gap={{ xs: 0.5, sm: 1 }}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Tooltip
                title={`Back ${windowDays} day${windowDays > 1 ? 's' : ''}`}
              >
                <IconButton
                  size="small"
                  onClick={() => shiftWindow(-1)}
                  sx={{ flexShrink: 0 }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
              <TextField
                type="date"
                label="From"
                size="small"
                value={startDate.format('YYYY-MM-DD')}
                onChange={(e) => {
                  const parsed = moment(e.target.value, 'YYYY-MM-DD', true)
                  if (parsed.isValid()) setStartDate(parsed.startOf('day'))
                }}
                InputLabelProps={{ shrink: true }}
                sx={dateFieldSx}
              />
              <TextField
                type="date"
                label="To"
                size="small"
                value={endDate.format('YYYY-MM-DD')}
                onChange={(e) => {
                  const parsed = moment(e.target.value, 'YYYY-MM-DD', true)
                  if (parsed.isValid()) setEndDate(parsed.endOf('day'))
                }}
                InputLabelProps={{ shrink: true }}
                sx={dateFieldSx}
              />
              <Tooltip
                title={`Forward ${windowDays} day${windowDays > 1 ? 's' : ''}`}
              >
                <IconButton
                  size="small"
                  onClick={() => shiftWindow(1)}
                  sx={{ flexShrink: 0 }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack direction="row" gap={1} flexWrap="wrap">
            {DATE_PRESETS.map(({ label, range }) => (
              <Chip
                key={label}
                label={label}
                size="small"
                color={activePreset === label ? 'anMainBlue' : 'default'}
                variant={activePreset === label ? 'filled' : 'outlined'}
                onClick={() => setRange(...range())}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Stack>
        </Stack>
      </Card>

      {/* --- Summary tiles (click to filter) --- */}
      <Stack direction="row" gap={1.5} flexWrap="wrap">
        <StatTile
          label="Journeys"
          value={bookings.length}
          caption="In selected range"
          color="anMainBlue"
          active={!groupFilter}
          onClick={() => setGroupFilter(null)}
        />
        <StatTile
          label="Awaiting crew"
          value={counts.Waiting}
          caption="Planned / assigned"
          color="info"
          active={groupFilter === 'Waiting'}
          onClick={() => toggleGroupFilter('Waiting')}
        />
        <StatTile
          label="Active"
          value={counts.Active}
          caption="Crew underway"
          color="primary"
          active={groupFilter === 'Active'}
          onClick={() => toggleGroupFilter('Active')}
        />
        <StatTile
          label="Completed"
          value={counts.Completed}
          caption="In selected range"
          color="success"
          active={groupFilter === 'Completed'}
          onClick={() => toggleGroupFilter('Completed')}
        />
        <StatTile
          label="Exceptions"
          value={counts.Exceptions}
          caption="Cancelled / no show / aborted"
          color="error"
          active={groupFilter === 'Exceptions'}
          onClick={() => toggleGroupFilter('Exceptions')}
        />
      </Stack>

      {/* --- Table --- */}
      {fetchError ? (
        <Alert severity="error">
          Failed to load bookings. Please try again.
        </Alert>
      ) : (
        <CustomDataGrid
          rows={visibleRows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row._id}
          rowHeight={58}
          disableRowSelectionOnClick
          onRowClick={({ row }) => navigate('/pts/bookings/' + row.booking)}
          hideToolbar
          columnVisibilityModel={columnVisibilityModel}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
        />
      )}
    </Stack>
  )
}

export default PTSBookings
